/**
 * TeamEvalsConfigSection: a team's nightly evals setup, set by staff
 * (TRA-1215). One form over PUT /api/internal/teams/:id/evals-config,
 * which creates the row when the team has none, so onboarding a customer
 * happens here and not in a migration.
 *
 * A benchmark is an entry: its source (a repository picked from the
 * team's GitHub App installation, or an S3 prefix on one of the team's
 * connections), the TaskHopper adapter that runs it, and the eval group it
 * fills. The installation and the S3 connections are linked on the
 * Connections page. One of them is the scored benchmark: the night reads
 * its tasks from that row's eval group and posts its runs to it, and
 * eval PRs publish into its repository. The row stores the group's slug,
 * so the picker is which row carries it.
 *
 * The same row carries the eval PR columns (the deny list and the
 * repository layout), which the Eval PRs page edits through the same
 * PUT. A save here sends them back as they were saved
 * (useEvalsConfigForm).
 *
 * The two other inputs decided elsewhere are read back beside the switch:
 * the default model (the team picks it on its Models page) and the
 * rollout secret names (the team enters them under Settings), so what the
 * night will find is visible where it is armed.
 */

import { Plus } from 'lucide-react'

import { getApiErrorDetail } from '@/api/client'
import { useListBenchmarkAdaptersApiBenchmarksAdaptersGet } from '@/api/generated/benchmarks/benchmarks'
import {
  useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet,
  useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet,
  useListTeamConnectionsApiInternalTeamsTeamIdConnectionsGet,
} from '@/api/generated/internal/internal'
import type {
  ConnectionAdminItem,
  GithubAppStatus,
  TeamEvalsConfigResponse,
} from '@/api/generated/model'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useEvalsConfigForm } from '@/hooks/useEvalsConfigForm'

import { BenchmarkSourceRow } from './BenchmarkSourceRow'
import type { EntryRow } from './benchmarkSources'
import { addBenchmark, removeBenchmark, scoredBenchmark } from './evalsConfigForm'
import { EvalsConfigFooter } from './EvalsConfigFooter'
import { TrainingFields } from './TrainingFields'
import type { AdapterChoices } from './HarnessFields'
import { s3ConnectionsOf } from './s3Connections'
import type { TeamConfigSectionProps } from './sections'

export function TeamEvalsConfigSection({
  section,
  team,
}: TeamConfigSectionProps): React.JSX.Element {
  const setup = useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet(team.id)
  // The benchmark rows pick a repository from the installation. Same read
  // and options as the Connections page (TeamGithubConnection), so the
  // two share one cache entry.
  const githubApp = useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet(team.id, {
    query: { retry: false },
  })
  const connections = useListTeamConnectionsApiInternalTeamsTeamIdConnectionsGet(team.id)
  const adapterList = useListBenchmarkAdaptersApiBenchmarksAdaptersGet()
  const loaded = setup.data?.status === 200 ? setup.data.data : undefined
  const status = githubApp.data?.status === 200 ? githubApp.data.data : undefined
  const assigned = connections.data?.status === 200 ? connections.data.data.connections : []
  const adapters: AdapterChoices = {
    adapters: adapterList.data?.status === 200 ? adapterList.data.data.adapters : [],
    pending: adapterList.isLoading,
    error: adapterList.isError
      ? (getApiErrorDetail(adapterList.error) ?? 'Failed to load the adapter list.')
      : null,
  }
  return (
    <div>
      <SettingsPageHeader section={section} />
      {setup.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : setup.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(setup.error) ?? 'Failed to load the evals config.'}
          </p>
        </SettingsBlock>
      ) : loaded === undefined ? null : (
        <EvalsConfigForm
          teamId={team.id}
          setup={loaded}
          github={status}
          githubPending={githubApp.isLoading}
          s3Connections={s3ConnectionsOf(assigned)}
          adapters={adapters}
        />
      )}
    </div>
  )
}

function EvalsConfigForm({
  teamId,
  setup,
  github,
  githubPending,
  s3Connections,
  adapters,
}: {
  teamId: string
  setup: TeamEvalsConfigResponse
  github: GithubAppStatus | undefined
  githubPending: boolean
  s3Connections: ConnectionAdminItem[]
  adapters: AdapterChoices
}): React.JSX.Element {
  const state = useEvalsConfigForm(teamId, setup, adapters.adapters)
  const { form, update } = state
  // Once the eval PR layout is saved, the scored benchmark's repository and
  // adapter are frozen with it (TeamEvalPrsSection says why), and the api
  // refuses a later change.
  const frozenGroup = setup.config?.tests_root != null ? setup.config.group_slug : null
  // The routes a training dataset of this team names: its scored benchmark's adapter's.
  const scoredAdapter = scoredBenchmark(form)?.harness.adapter
  const scoredRoutes = (
    adapters.adapters.find((item) => item.name === scoredAdapter)?.routes ?? []
  ).map((route) => route.name)
  const updateEntry = (index: number, entry: EntryRow): void => {
    state.edit((previous) => ({
      ...previous,
      benchmark_sources: previous.benchmark_sources.map((row, i) => (i === index ? entry : row)),
    }))
  }

  return (
    <form onSubmit={state.submit}>
      <SettingsSection
        title="Scoring"
        description="Once a merged test is imported, the runner fetches the scored benchmark and scores the team's default model on it. One run per test lands in the eval group."
      >
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Switch
              id="evals-enabled"
              checked={form.evals_enabled}
              onCheckedChange={(checked) => { update('evals_enabled', checked) }}
            />
            <Label htmlFor="evals-enabled">Score this team's merged tests</Label>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Switch
                id="task-pipeline-enabled"
                checked={form.task_pipeline_enabled}
                onCheckedChange={(checked) => {
                  // Training trains what the gate proves, so it goes off with it.
                  state.edit((previous) => ({
                    ...previous,
                    task_pipeline_enabled: checked,
                    training_enabled: checked && previous.training_enabled,
                  }))
                }}
              />
              <Label htmlFor="task-pipeline-enabled">
                Generate and gate training tasks
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              After a scoring run, task generation starts on the failure records this team
              has not generated from. A team with no record waiting gets tasks from its newly
              scored tests. The task gate follows for the batches that wait for it, and the
              training dataset build runs once the gate has proved a task the newest dataset
              lacks. Each job starts at the threshold set on the team&apos;s Pipeline page
              and runs for one team at a time. Task generation counts failure records toward
              its threshold, so a start on newly scored tests does not wait for it.
            </p>
          </div>
          {form.task_pipeline_enabled && (
            <TrainingFields
              idPrefix="evals-config"
              values={{
                enabled: form.training_enabled,
                preset: form.training_preset,
                maxSteps: form.training_max_steps,
              }}
              routes={scoredRoutes}
              onChange={(changes) => {
                state.edit((previous) => ({
                  ...previous,
                  training_enabled: changes.enabled ?? previous.training_enabled,
                  training_preset:
                    changes.preset === undefined ? previous.training_preset : changes.preset,
                  training_max_steps:
                    changes.maxSteps === undefined ? previous.training_max_steps : changes.maxSteps,
                }))
              }}
            />
          )}
          <NightInputs setup={setup} />
        </div>
      </SettingsSection>

      <SettingsSection
        title="Benchmarks"
        description="A benchmark is read from GitHub or S3 and run by a TaskHopper adapter. Each one fills its own eval group."
      >
        <BenchmarksEditor
          entries={form.benchmark_sources}
          github={github}
          githubPending={githubPending}
          connections={s3Connections}
          adapters={adapters}
          frozenGroup={frozenGroup}
          onChange={updateEntry}
          onAdd={() => { state.edit(addBenchmark) }}
          onRemove={(index) => { state.edit((previous) => removeBenchmark(previous, index)) }}
        />
      </SettingsSection>

      <SettingsSection
        title="Scored benchmark"
        description="The night reads its tasks from this benchmark's eval group and posts its runs to it. Eval PRs publish into its repository."
      >
        <ScoredBenchmarkPicker
          entries={form.benchmark_sources}
          value={form.scored_benchmark}
          onChange={(index) => { update('scored_benchmark', index) }}
        />
      </SettingsSection>

      <EvalsConfigFooter state={state} />
    </form>
  )
}

function BenchmarksEditor({
  entries,
  github,
  githubPending,
  connections,
  adapters,
  frozenGroup,
  onChange,
  onAdd,
  onRemove,
}: {
  entries: EntryRow[]
  github: GithubAppStatus | undefined
  githubPending: boolean
  connections: ConnectionAdminItem[]
  adapters: AdapterChoices
  /** The saved scored group when the eval PR layout is saved: its repository and adapter are frozen. */
  frozenGroup: string | null
  onChange: (index: number, entry: EntryRow) => void
  onAdd: () => void
  onRemove: (index: number) => void
}): React.JSX.Element {
  return (
    <div className="space-y-4">
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No benchmarks yet. The night has nothing to score until one is added.
        </p>
      ) : (
        <ul className="space-y-6">
          {entries.map((entry, index) => (
            <BenchmarkSourceRow
              // Rows are appended and removed, never reordered.
              key={index}
              index={index}
              entry={entry}
              github={github}
              githubPending={githubPending}
              connections={connections}
              adapters={adapters}
              frozen={frozenGroup !== null && entry.group.slug.trim() === frozenGroup}
              onChange={(next) => { onChange(index, next) }}
              onRemove={() => { onRemove(index) }}
            />
          ))}
        </ul>
      )}
      <Button type="button" variant="outline" size="sm" onClick={onAdd}>
        <Plus aria-hidden="true" />
        Add benchmark
      </Button>
    </div>
  )
}

/**
 * Which of the benchmark rows the night scores. The rows are the options,
 * numbered as the editor numbers them, each with the eval group it fills.
 * One row is picked by itself; none leaves the picker disabled.
 */
function ScoredBenchmarkPicker({
  entries,
  value,
  onChange,
}: {
  entries: EntryRow[]
  value: number | null
  onChange: (index: number) => void
}): React.JSX.Element {
  const id = 'evals-scored-benchmark'
  return (
    <div className="max-w-xl space-y-2">
      <Label htmlFor={id}>Scored benchmark</Label>
      <Select
        value={value === null ? '' : String(value)}
        disabled={entries.length === 0}
        onValueChange={(picked) => { onChange(Number(picked)) }}
      >
        <SelectTrigger id={id} aria-describedby={`${id}-hint`}>
          <SelectValue placeholder="No benchmark picked" />
        </SelectTrigger>
        <SelectContent>
          {entries.map((entry, index) => {
            const slug = entry.group.slug.trim()
            return (
              // Rows are appended and removed, never reordered.
              <SelectItem key={index} value={String(index)}>
                Benchmark {String(index + 1)}
                {slug !== '' && <span className="ml-2 font-mono text-xs">{slug}</span>}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {entries.length === 0
          ? 'Add a benchmark above first. The night needs one to score.'
          : 'Its eval group slug and name are set on the row above.'}
      </p>
    </div>
  )
}

/**
 * What the night will find that is set elsewhere. Names only: the model
 * comes from the team's Models page, the secrets from its Settings. The
 * installation has its own section above.
 */
function NightInputs({ setup }: { setup: TeamEvalsConfigResponse }): React.JSX.Element {
  const rows: [string, React.ReactNode][] = [
    [
      'Scored model',
      setup.default_model === null ? (
        <Missing>None. The team picks one on its Models page.</Missing>
      ) : (
        <Mono>{setup.default_model}</Mono>
      ),
    ],
    [
      'Rollout secrets',
      setup.rollout_env_names.length === 0 ? (
        <Missing>None stored. The team enters them under Settings, Rollout secrets.</Missing>
      ) : (
        <Mono>{setup.rollout_env_names.join(', ')}</Mono>
      ),
    ],
  ]
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="pt-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {label}
          </dt>
          <dd className="m-0 min-w-0">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function Mono({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <span className="break-all font-mono text-xs">{children}</span>
}

function Missing({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <span className="text-sm text-muted-foreground">{children}</span>
}
